/**
 * Segment operations (W2-11 batch-tools; DESIGN.md §5.6.5; WORKPACKAGES Appendix B `segments.extrapolate|join|
 * reverse|link|unlink|selectLinked|splitRpe|bake`). Motion menu, context menus, inspector buttons and palette.
 *
 *  - Extrapolate (RPE Pass, EVT-17), Reverse (exact), Link / Unlink (binding group, EVT-21): one transaction over
 *    the selected segments through `Core.keys`.
 *  - Join: every selected key between two segments (or between two selected segments) whose neighbours merge
 *    EXACTLY into one segment; the candidates are tried on a scratch copy first, so the joinable ones are joined
 *    in one transaction and the others reported (with a Simplify action for curves that only merge approximately).
 *  - Select linked: adds every segment of the selected segments' link groups to the selection.
 *  - Split (RPE linear, EVT-18): RPE's split at the playhead on the focused column (palette only; changes motion).
 *  - Bake (RPE Cut, EVT-15) quick action: pieces every (1/snap)/density beat (density from Settings, default 8),
 *    anchored on the grid line nearest the playhead; X/Y-linked partners are baked too. `tools.bake` opens the
 *    sheet with more options.
 */

import type { CommandContext, CommandDef } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import { createDocContainer } from '../../core/doc/container';
import { createTxDraft } from '../../core/doc/txDraft';
import type { ChartDoc, DocIndexes, KeyId, LineProp, TrackId } from '../../core/model/types';
import type { SelectionSnapshot } from '../../core/ops/types';
import type { Beat } from '../../core/time/types';
import { Beats } from '../../core/time/beat';
import type { MessageRef } from '../../core/util/types';
import { m } from './batch.messages';
import {
  DEFAULT_BAKE_DENSITY,
  EXT,
  extNumber,
  playheadBeat,
  ref,
  selectionIds,
  snapDiv,
  transactWithToasts,
  type Enabled,
} from './batchContext';

const NO_SELECTION: SelectionSnapshot = {
  notes: [],
  keys: [],
  segments: [],
  lines: [],
  bpm: [],
  markers: [],
  effects: [],
  videos: [],
  groups: [],
  activeLine: null,
};

/** Largest bake divisor the key commands accept. */
export const MAX_BAKE_DIV = 1920 * 16;

interface View {
  readonly doc: ChartDoc;
  readonly idx: DocIndexes;
}

/** Selected segments (start keys that have a next key); keys selected as points count when no segment is. */
export function selectedSegments(ctx: Pick<CommandContext, 'store' | 'editor'>): KeyId[] {
  const store = ctx.store;
  if (store === null) return [];
  const ids = selectionIds(ctx);
  const base = ids.segments.length > 0 ? ids.segments : ids.keys;
  return base.filter((id) => store.idx.nextKey(id) !== undefined);
}

function needSegments(ctx: CommandContext, min = 1): Enabled {
  if (ctx.store === null) return ref(m.reasonNoProject);
  const n = selectedSegments(ctx).length;
  if (n === 0) return ref(m.reasonNoSegments);
  if (n < min) return ref(m.reasonNeedTwoSegments);
  return true;
}

/** Keys a Join would remove: selected interior keys, and starts of selected segments that follow another one. */
export function joinCandidates(
  view: View,
  sel: { readonly keys: readonly KeyId[]; readonly segments: readonly KeyId[] },
): KeyId[] {
  const out = new Set<KeyId>();
  for (const id of sel.keys) {
    if (view.idx.prevKey(id) !== undefined && view.idx.nextKey(id) !== undefined) out.add(id);
  }
  const segs = new Set(sel.segments);
  for (const id of sel.segments) {
    const prev = view.idx.prevKey(id);
    if (prev !== undefined && segs.has(prev) && view.idx.nextKey(id) !== undefined) out.add(id);
  }
  return [...out].sort((a, b) => {
    const ka = view.doc.keys.get(a);
    const kb = view.doc.keys.get(b);
    return ka === undefined || kb === undefined ? 0 : Beats.cmp(ka.t, kb.t) || a - b;
  });
}

/**
 * Tries Join on every candidate over a scratch copy of the document (in order, each on the result of the previous
 * successful ones). Returns the joinable keys and the reason the first other one could not be joined.
 */
export function dryRunJoins(
  doc: ChartDoc,
  candidates: readonly KeyId[],
): { joinable: KeyId[]; firstReason: MessageRef | null } {
  const c = createDocContainer(doc);
  const joinable: KeyId[] = [];
  let firstReason: MessageRef | null = null;
  for (const key of candidates) {
    if (!c.doc.keys.has(key)) continue;
    const draft = createTxDraft(c, NO_SELECTION);
    const r = draft.run((tx) => {
      Core.keys.joinAt(tx, { key });
    });
    if (r.ok) {
      c.applyAll(draft.finish().ops);
      joinable.push(key);
    } else {
      draft.abort();
      firstReason ??= r.reason;
    }
  }
  return { joinable, firstReason };
}

/** Segments of the other position axis over the same spans (lines whose X/Y are not linked; linked ones follow anyway). */
export function partnerSegments(view: View, segments: readonly KeyId[]): KeyId[] {
  const out = new Set<KeyId>();
  for (const id of segments) {
    const k = view.doc.keys.get(id);
    const nextId = view.idx.nextKey(id);
    const n = nextId === undefined ? undefined : view.doc.keys.get(nextId);
    const owner = k === undefined ? undefined : view.doc.tracks.get(k.track)?.owner;
    if (k === undefined || n === undefined || owner?.kind !== 'line') continue;
    if (owner.prop !== 'moveX' && owner.prop !== 'moveY') continue;
    if (view.doc.lines.get(owner.line)?.editor.xyLinked === true) continue;
    const other: LineProp = owner.prop === 'moveX' ? 'moveY' : 'moveX';
    const track = view.idx.track(owner.line, other, owner.layer);
    if (track === undefined) continue;
    for (const pid of view.idx.keysByTrack(track)) {
      const pk = view.doc.keys.get(pid);
      const pnId = view.idx.nextKey(pid);
      const pn = pnId === undefined ? undefined : view.doc.keys.get(pnId);
      if (pk === undefined || pn === undefined) continue;
      if (Beats.lt(pk.t, n.t) && Beats.lt(k.t, pn.t)) out.add(pid);
    }
  }
  return [...out];
}

/** True when some selected segment is a position segment of a line whose X/Y are not linked. */
export function hasUnlinkedPartner(view: View, segments: readonly KeyId[]): boolean {
  return partnerSegments(view, segments).length > 0;
}

/** Bake divisor for a density (pieces per snap step). */
export function bakeDiv(snap: number, density: number): number {
  return Math.min(MAX_BAKE_DIV, Math.max(1, Math.round(snap * density)));
}

/** Track a command acts on: the focused column's, else the track of the first selected segment or key. */
function focusedTrack(ctx: CommandContext): TrackId | null {
  const store = ctx.store;
  if (store === null) return null;
  const col = ctx.editor.getState().focusedColumn;
  if (col !== null) {
    const t = store.idx.track(col.line, col.prop, col.layer);
    if (t !== undefined) return t;
  }
  const ids = selectionIds(ctx);
  const k = ids.keyish[0];
  return k === undefined ? null : (store.doc.keys.get(k)?.track ?? null);
}

function playheadInside(ctx: CommandContext, track: TrackId): boolean {
  const store = ctx.store;
  if (store === null) return false;
  const at = playheadBeat(ctx);
  const ids = store.idx.keysByTrack(track);
  const first = ids[0] === undefined ? undefined : store.doc.keys.get(ids[0]);
  const lastId = ids[ids.length - 1];
  const last = lastId === undefined ? undefined : store.doc.keys.get(lastId);
  return first !== undefined && last !== undefined && Beats.lt(first.t, at) && Beats.lt(at, last.t);
}

function segmentCount(ids: readonly KeyId[]): Readonly<Record<string, number>> {
  return { count: ids.length };
}

export const SEGMENT_COMMANDS: readonly CommandDef[] = [
  {
    id: 'segments.extrapolate',
    title: m.cmdExtrapolate,
    category: 'Motion',
    icon: 'TrendingUp',
    aliases: ['pass', 'RPE Pass', '延续'],
    level: 'full',
    enabled: (ctx) => needSegments(ctx),
    run(ctx) {
      const segments = selectedSegments(ctx);
      transactWithToasts(ctx, { label: ref(m.txnExtrapolate), command: 'segments.extrapolate' }, (tx) => {
        Core.keys.extrapolate(tx, { segments });
      });
    },
    menus: [{ menu: 'motion.context', group: 'segments', order: 30 }],
  },
  {
    id: 'segments.join',
    title: m.cmdJoin,
    category: 'Motion',
    icon: 'Merge',
    aliases: ['merge segments', 'join keys'],
    level: 'guided',
    enabled(ctx) {
      const store = ctx.store;
      if (store === null) return ref(m.reasonNoProject);
      const ids = selectionIds(ctx);
      return joinCandidates(store, ids).length > 0 ? true : ref(m.reasonJoinSelect);
    },
    run(ctx) {
      const store = ctx.store;
      if (store === null) return;
      const ids = selectionIds(ctx);
      const candidates = joinCandidates(store, ids);
      const { joinable, firstReason } = dryRunJoins(store.doc, candidates);
      if (joinable.length === 0) {
        ctx.ui.toast(firstReason ?? ref(m.reasonJoinSelect), {
          kind: 'warning',
          action: { label: ref(m.simplifyInstead), command: 'tools.bake', args: { mode: 'simplify' } },
        });
        return;
      }
      const out = transactWithToasts(
        ctx,
        { label: ref(m.txnJoin), command: 'segments.join' },
        (tx) => {
          for (const key of joinable) if (tx.doc.keys.has(key)) Core.keys.joinAt(tx, { key });
        },
        () => ref(m.joinDone, { count: joinable.length }),
      );
      const skipped = candidates.length - joinable.length;
      if (out?.ok === true && skipped > 0) {
        ctx.ui.toast(ref(m.joinSkipped, { count: skipped }), {
          kind: 'info',
          action: { label: ref(m.simplifyInstead), command: 'tools.bake', args: { mode: 'simplify' } },
        });
      }
    },
    menus: [{ menu: 'motion.context', group: 'segments', order: 40 }],
  },
  {
    id: 'segments.reverse',
    title: m.cmdReverse,
    category: 'Motion',
    icon: 'ArrowLeftRight',
    aliases: ['reverse segments', 'mirror time'],
    level: 'guided',
    enabled: (ctx) => needSegments(ctx),
    run(ctx) {
      const segments = selectedSegments(ctx);
      transactWithToasts(
        ctx,
        { label: ref(m.txnReverse), command: 'segments.reverse' },
        (tx) => {
          Core.keys.reverseSegments(tx, { segments });
        },
        () => ref(m.reverseDone, segmentCount(segments)),
      );
    },
    menus: [{ menu: 'motion.context', group: 'segments', order: 50 }],
  },
  {
    id: 'segments.link',
    title: m.cmdLink,
    category: 'Motion',
    icon: 'Link',
    aliases: ['binding group', 'group', 'RPE group', '绑定'],
    level: 'full',
    enabled: (ctx) => needSegments(ctx, 2),
    run(ctx) {
      const segments = selectedSegments(ctx);
      const primarySel = ctx.editor.getState().selection.primary;
      const primary =
        primarySel !== null &&
        (primarySel.kind === 'segment' || primarySel.kind === 'key') &&
        segments.includes(primarySel.id as KeyId)
          ? (primarySel.id as KeyId)
          : (segments[0] as KeyId);
      transactWithToasts(
        ctx,
        { label: ref(m.txnLink), command: 'segments.link' },
        (tx) => Core.keys.linkSegments(tx, { segments, primary }),
        () => ref(m.linkDone, segmentCount(segments)),
      );
    },
    menus: [{ menu: 'motion.context', group: 'link', order: 10 }],
  },
  {
    id: 'segments.unlink',
    title: m.cmdUnlink,
    category: 'Motion',
    icon: 'Unlink',
    aliases: ['ungroup'],
    level: 'full',
    enabled(ctx) {
      const r = needSegments(ctx);
      if (r !== true) return r;
      const store = ctx.store;
      return selectedSegments(ctx).some((id) => (store?.doc.keys.get(id)?.link ?? 0) !== 0)
        ? true
        : ref(m.reasonNotLinked);
    },
    run(ctx) {
      const segments = selectedSegments(ctx);
      transactWithToasts(ctx, { label: ref(m.txnUnlink), command: 'segments.unlink' }, (tx) => {
        Core.keys.unlinkSegments(tx, { segments });
      });
    },
    menus: [{ menu: 'motion.context', group: 'link', order: 20 }],
  },
  {
    id: 'segments.selectLinked',
    title: m.cmdSelectLinked,
    category: 'Select',
    icon: 'Link2',
    level: 'full',
    enabled(ctx) {
      const r = needSegments(ctx);
      if (r !== true) return r;
      const store = ctx.store;
      return selectedSegments(ctx).some((id) => (store?.doc.keys.get(id)?.link ?? 0) !== 0)
        ? true
        : ref(m.reasonNotLinked);
    },
    run(ctx) {
      const store = ctx.store;
      if (store === null) return;
      const links = new Set<number>();
      for (const id of selectedSegments(ctx)) {
        const l = store.doc.keys.get(id)?.link ?? 0;
        if (l !== 0) links.add(l);
      }
      const members = [...links].flatMap((l) => [...store.idx.linkMembers(l)]);
      ctx.editor.getState().select({ segments: new Set(members) }, 'add');
      ctx.ui.toast(ref(m.selectedLinked, { count: members.length }), { kind: 'info' });
    },
    menus: [{ menu: 'motion.context', group: 'link', order: 30 }],
  },
  {
    id: 'segments.splitRpe',
    title: m.cmdSplitRpe,
    category: 'Motion',
    icon: 'SplitSquareVertical',
    aliases: ['RPE split', 'split linear', '分割'],
    level: 'full',
    enabled(ctx) {
      if (ctx.store === null) return ref(m.reasonNoProject);
      const track = focusedTrack(ctx);
      if (track === null) return ref(m.reasonNoColumn);
      return playheadInside(ctx, track) ? true : ref(m.reasonPlayheadOutside);
    },
    run(ctx) {
      const track = focusedTrack(ctx);
      if (track === null) return;
      const t: Beat = playheadBeat(ctx);
      transactWithToasts(
        ctx,
        { label: ref(m.txnSplitRpe), command: 'segments.splitRpe' },
        (tx) => Core.keys.splitRpeLinear(tx, { track, t }),
        () => ref(m.splitRpeDone),
      );
    },
  },
  {
    id: 'segments.bake',
    title: m.cmdBakeQuick,
    category: 'Motion',
    icon: 'Scissors',
    aliases: ['cut', 'RPE cut', '切割', 'bake'],
    level: 'guided',
    enabled: (ctx) => needSegments(ctx),
    run(ctx) {
      const segments = selectedSegments(ctx);
      const density = extNumber(ctx, EXT.bakeDensity, DEFAULT_BAKE_DENSITY, 1, 64);
      const div = bakeDiv(snapDiv(ctx), density);
      const anchor = playheadBeat(ctx);
      transactWithToasts(
        ctx,
        { label: ref(m.txnBake), command: 'segments.bake' },
        (tx) => Core.keys.bakeSegments(tx, { segments, div, anchor }),
        (r) => ref(m.bakeDone, { count: r.pieces }),
      );
    },
    menus: [{ menu: 'motion.context', group: 'segments', order: 20 }],
  },
];
